import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OkIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/HighlightOff";
import { useDispatch } from "react-redux";

import {
  patchDepartment,
  postRcDepartment,
  softDeleteDepartment
} from "../../slices/departmentSlice";
import { t, tl } from "../../components/translate";
import useGetDepartments from "../../hooks/department";
import OkhatiDialog from "../../components/Dialog/Dialog";
import { notificationAdd } from "../../actions/notification";
import { Department as DepartmentInterface } from "../../interfaces/DepartmentInterface";

interface IDepartmetState {
  id: undefined | number;
  name: string;
  shortCode: string;
}

const initialDeptState = {
  id: undefined,
  name: "",
  shortCode: ""
};

export default function Department(): React.ReactNode {
  const dispatch = useDispatch();

  const [department, setDepartment] = React.useState<DepartmentInterface>(
    {} as DepartmentInterface
  );
  const departments = useGetDepartments({ showDeletedDepartments: false });

  const changeHandler = (value: string | boolean, changer: keyof DepartmentInterface) => {
    setDepartment({ ...department, [changer]: value });
  };
  const [selectedDeptId, setSelectedDeptId] = React.useState<number | undefined>(undefined);
  const [editingDepartment, setEditingDepartment] = React.useState<IDepartmetState>({
    ...initialDeptState
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* department inline form */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ minWidth: "200px" }}>
          <TextField
            fullWidth
            value={department?.name || ""}
            onChange={(e) => changeHandler(e.target.value, "name")}
            variant="outlined"
            label={tl("department.name")}
            placeholder={t("deptName")}
            slotProps={{
              inputLabel: { shrink: true }
            }}
          />
        </Box>

        <Box sx={{ minWidth: "200px" }}>
          <TextField
            fullWidth
            value={department?.shortCode || ""}
            onChange={(e) => changeHandler(e.target.value, "shortCode")}
            variant="outlined"
            label={tl("department.shortCode")}
            placeholder={t("department.shortCode")}
            slotProps={{
              inputLabel: { shrink: true }
            }}
          />
        </Box>

        <Button
          sx={{ maxWidth: "200px" }}
          onClick={() => {
            dispatch(postRcDepartment(department));
            setDepartment({
              name: "",
              shortCode: ""
            } as DepartmentInterface);
          }}
          disabled={!department?.name || !department?.shortCode}
          variant="contained"
        >
          {tl("saveDepartment")}
        </Button>
      </Box>

      <Divider />

      {/* department list */}
      <Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>{tl("ID")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>{tl("Name")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Short Code</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...departments]
                .sort((a, b) => a.id - b.id)
                .map((dept) => {
                  const isEditing = editingDepartment.id === dept.id;

                  return (
                    <TableRow key={dept.id}>
                      <TableCell>
                        <Typography>{dept.id}</Typography>
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editingDepartment.name}
                            onChange={(e) => {
                              setEditingDepartment((prevState) => ({
                                ...prevState,
                                name: e.target.value
                              }));
                            }}
                            variant="outlined"
                          />
                        ) : (
                          <Typography>{dept.name}</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editingDepartment.shortCode}
                            onChange={(e) => {
                              setEditingDepartment((prevState) => ({
                                ...prevState,
                                shortCode: e.target.value
                              }));
                            }}
                            slotProps={{
                              htmlInput: {
                                maxLength: 3
                              }
                            }}
                            variant="outlined"
                          />
                        ) : (
                          <Typography>{dept.shortCode}</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <Box display="flex">
                            <OkIcon
                              sx={{ cursor: "pointer", mr: 1 }}
                              onClick={() => {
                                if (!editingDepartment.name || !editingDepartment.shortCode) {
                                  dispatch(
                                    notificationAdd({
                                      id: new Date().getUTCMilliseconds(),
                                      variant: "error",
                                      message: "Name and short code are required for department.",
                                      autoTimeout: true
                                    })
                                  );
                                  return;
                                }

                                if (editingDepartment.id) {
                                  dispatch(
                                    patchDepartment(editingDepartment as DepartmentInterface)
                                  );
                                } else {
                                  dispatch(
                                    notificationAdd({
                                      id: new Date().getUTCMilliseconds(),
                                      variant: "error",
                                      message: "Error updating department.",
                                      autoTimeout: true
                                    })
                                  );
                                }
                                setEditingDepartment({
                                  ...initialDeptState
                                });
                              }}
                            />
                            <CancelIcon
                              onClick={() => setEditingDepartment({ ...initialDeptState })}
                              sx={{ cursor: "pointer" }}
                            />
                          </Box>
                        ) : (
                          <Box display="flex">
                            <EditIcon
                              sx={{
                                cursor: "pointer",
                                fontSize: "16px",
                                color: "rgba(0, 0, 0, 0.54)",
                                mr: 1
                              }}
                              onClick={() => {
                                setEditingDepartment(dept);
                              }}
                            />
                            <DeleteIcon
                              sx={{
                                cursor: "pointer",
                                fontSize: "16px",
                                color: "rgba(0, 0, 0, 0.54)"
                              }}
                              onClick={() => {
                                setSelectedDeptId(dept.id);
                              }}
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <OkhatiDialog
        title="Warning"
        next={() => {
          dispatch(softDeleteDepartment({ id: selectedDeptId }));
          setSelectedDeptId(undefined);
        }}
        cancel={() => {
          setSelectedDeptId(undefined);
        }}
        readMode={false}
        description="Are you sure you want to delete department?"
        open={Boolean(selectedDeptId)}
      />
    </Box>
  );
}
